import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';

// Thunks
export const fetchPlans = createAsyncThunk('pricing/fetchPlans', async (_,{rejectWithValue}) => {
	try {
		const { data } = await axiosInstance.get('/admin/pricing');
		return data.data;

	} catch (error) {
		return rejectWithValue(error.response?.data || { message: error.message });
	}
});

export const createPlan = createAsyncThunk('pricing/createPlan', async (payload,{rejectWithValue}) => {
	try {
		const { data } = await axiosInstance.post('/admin/pricing', payload);
		return data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || { message: error.message });
	}
});

export const updatePlan = createAsyncThunk('pricing/updatePlan', async ({ id, updates }, { rejectWithValue }) => {
	try {
		const { data } = await axiosInstance.put(`/admin/pricing/${id}`, updates);
		return data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || { message: error.message });
	}
});

export const deletePlan = createAsyncThunk('pricing/deletePlan', async (id, { rejectWithValue }) => {
	try {
		await axiosInstance.delete(`/admin/pricing/${id}`);
		return id;
	} catch (error) {
		return rejectWithValue(error.response?.data || { message: error.message });
	}
});

export const setPlanVisibility = createAsyncThunk('pricing/setVisibility', async ({ id, visible }, { rejectWithValue }) => {
	try {
		const { data } = await axiosInstance.patch(`/admin/pricing/${id}/visibility`, { visible });
		return data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || { message: error.message });
	}
});

export const setPlansOrder = createAsyncThunk('pricing/setOrder', async (orders, { rejectWithValue }) => {
	try {
		const { data } = await axiosInstance.patch('/admin/pricing/order', { orders });
		return data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || { message: error.message });
	}
});

export const setPlanFeaturesApi = createAsyncThunk(
	'pricing/setPlanFeaturesApi',
	async ({ id, features }, { rejectWithValue }) => {
		try {
			const { data } = await axiosInstance.patch(`/admin/pricing/${id}/features`, { features });
			return data.data;
		} catch (error) {
			return rejectWithValue(error.response?.data || { message: error.message });
		}
	}
);

export const setPlanLimitationsApi = createAsyncThunk(
	'pricing/setPlanLimitationsApi',
	async ({ id, limitations }, { rejectWithValue }) => {
		try {
			const { data } = await axiosInstance.patch(`/admin/pricing/${id}/limitations`, { limitations });
			return data.data;
		} catch (error) {
			return rejectWithValue(error.response?.data || { message: error.message });
		}
	}
);

// Optional: global PaymentFeatures library
export const fetchPaymentFeatures = createAsyncThunk(
	'pricing/fetchPaymentFeatures',
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axiosInstance.get('/admin/payment-features');
			return data.data;
		} catch (error) {
			return rejectWithValue(error.response?.data || { message: error.message });
		}
	}
);

export const upsertPaymentFeatures = createAsyncThunk(
	'pricing/upsertPaymentFeatures',
	async ({ limitations, features }, { rejectWithValue }) => {
		try {
			const { data } = await axiosInstance.put('/admin/payment-features', { limitations, features });
			return data.data;
		} catch (error) {
			return rejectWithValue(error.response?.data || { message: error.message });
		}
	}
);

// Slice
const pricingSlice = createSlice({
	name: 'pricing',
	initialState: {
		plans: [],
		loading: false,
		error: null,
		library: { limitations: null, features: [] },
	},
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchPlans.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchPlans.fulfilled, (s, a) => { 
				s.loading = false; 
				s.plans = a.payload; 
			})
			.addCase(fetchPlans.rejected, (s, a) => { s.loading = false; s.error = a.error.message; })
			.addCase(createPlan.pending, (s) => { s.error = null; })
			.addCase(createPlan.rejected, (s, a) => { s.error = a.error.message; })
			.addCase(createPlan.fulfilled, (s, a) => { 
				s.plans.push(a.payload); 
			})
			.addCase(updatePlan.fulfilled, (s, a) => {
				s.plans = s.plans.map(p => p._id === a.payload._id ? a.payload : p);
			})
			.addCase(deletePlan.fulfilled, (s, a) => {
				s.plans = s.plans.filter(p => p._id !== a.payload);
			})
			.addCase(setPlanVisibility.fulfilled, (s, a) => {
				s.plans = s.plans.map(p => p._id === a.payload._id ? a.payload : p);
			})
			.addCase(setPlansOrder.fulfilled, (s, a) => {
				const map = new Map(a.payload.map(p => [p._id, p.order]));
				s.plans = s.plans.map(p => ({ ...p, order: map.get(p._id) ?? p.order })).sort((x, y) => x.order - y.order);
			})
			.addCase(setPlanFeaturesApi.fulfilled, (s, a) => {
			s.plans = s.plans.map(p => p._id === a.payload._id ? a.payload : p);
		})
		.addCase(setPlanLimitationsApi.fulfilled, (s, a) => {
			s.plans = s.plans.map(p => p._id === a.payload._id ? a.payload : p);
		})
		.addCase(fetchPaymentFeatures.fulfilled, (s, a) => {
			s.library = a.payload || { limitations: null, features: [] };
		})
		.addCase(upsertPaymentFeatures.fulfilled, (s, a) => {
			s.library = a.payload || { limitations: null, features: [] };
		});
	}
});

export default pricingSlice.reducer;